import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export interface LiveFinding {
  rule: string;
  severity: string;
  explanation_for_user: string;
  repair_hint: string;
  evidence?: { expected?: string; observed?: string };
}

export interface TimedSegment {
  type: 'commentary' | 'quran';
  text: string;
  start_ms: number;
  end_ms: number;
}

export interface LiveMessage {
  role: 'teacher' | 'student';
  text: string;
  audioUrl?: string;
  score?: number;
  findings?: LiveFinding[];
  playReference?: boolean;
  timedSegments?: TimedSegment[];
  timestamp: number;
}

export interface SessionSummary {
  totalTurns: number;
  averageScore: number;
  rulesPracticed: string[];
  durationS: number;
}

interface LiveSessionState {
  status: 'idle' | 'connecting' | 'active' | 'ended';
  sessionId: string | null;
  surah: number;
  ayah: number;
  surahName: string;
  ayahText: string;
  phase: 'greeting' | 'listening' | 'processing' | 'feedback' | 'summary';
  messages: LiveMessage[];
  currentScore: number | null;
  isTeacherSpeaking: boolean;
  /** Index of the message currently being played (for karaoke) */
  playingMessageIndex: number | null;
  /** Current playback time in ms (for karaoke highlighting) */
  playbackTimeMs: number | null;
  processingStep: string | null;
  summary: SessionSummary | null;
  error: string | null;
  usage: { used: number; limit: number } | null;
}

const initialState: LiveSessionState = {
  status: 'idle',
  sessionId: null,
  surah: 1,
  ayah: 1,
  surahName: '',
  ayahText: '',
  phase: 'greeting',
  messages: [],
  currentScore: null,
  isTeacherSpeaking: false,
  playingMessageIndex: null,
  playbackTimeMs: null,
  processingStep: null,
  summary: null,
  error: null,
  usage: null,
};

const liveSessionSlice = createSlice({
  name: 'liveSession',
  initialState,
  reducers: {
    setStatus(state, action: PayloadAction<LiveSessionState['status']>) {
      state.status = action.payload;
    },
    setSessionInfo(state, action: PayloadAction<{
      sessionId: string;
      surah: number;
      ayah: number;
      surahName: string;
      ayahText: string;
    }>) {
      state.sessionId = action.payload.sessionId;
      state.surah = action.payload.surah;
      state.ayah = action.payload.ayah;
      state.surahName = action.payload.surahName;
      state.ayahText = action.payload.ayahText;
    },
    setPhase(state, action: PayloadAction<LiveSessionState['phase']>) {
      state.phase = action.payload;
    },
    addMessage(state, action: PayloadAction<LiveMessage>) {
      state.messages.push(action.payload);
    },
    setCurrentScore(state, action: PayloadAction<number | null>) {
      state.currentScore = action.payload;
    },
    setTeacherSpeaking(state, action: PayloadAction<boolean>) {
      state.isTeacherSpeaking = action.payload;
      if (!action.payload) {
        state.playingMessageIndex = null;
        state.playbackTimeMs = null;
      }
    },
    setPlayback(state, action: PayloadAction<{ messageIndex: number; timeMs: number } | null>) {
      if (action.payload) {
        state.playingMessageIndex = action.payload.messageIndex;
        state.playbackTimeMs = action.payload.timeMs;
      } else {
        state.playingMessageIndex = null;
        state.playbackTimeMs = null;
      }
    },
    setProcessingStep(state, action: PayloadAction<string | null>) {
      state.processingStep = action.payload;
    },
    setSummary(state, action: PayloadAction<SessionSummary>) {
      state.summary = action.payload;
    },
    setUsage(state, action: PayloadAction<{ used: number; limit: number }>) {
      state.usage = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    resetSession() {
      return initialState;
    },
  },
});

export const {
  setStatus,
  setSessionInfo,
  setPhase,
  addMessage,
  setCurrentScore,
  setTeacherSpeaking,
  setPlayback,
  setProcessingStep,
  setSummary,
  setUsage,
  setError,
  resetSession,
} = liveSessionSlice.actions;

export default liveSessionSlice.reducer;
